import { useSyncExternalStore } from "react";
import type { Col, GameInfo, Member, Presence, ChatMsg, Layer, Page, Obj, Char, Handout, Macro, RollTable, Track, Deck } from "../shared/types";

export interface Store<T> {
  get(): T;
  set(p: Partial<T> | ((s: T) => Partial<T>)): void;
  subscribe(f: () => void): () => void;
}

export function createStore<T>(init: T): Store<T> {
  let state = init;
  const subs = new Set<() => void>();
  return {
    get: () => state,
    set(p) {
      const patch = typeof p === "function" ? (p as any)(state) : p;
      state = { ...state, ...patch };
      subs.forEach(f => f());
    },
    subscribe(f) {
      subs.add(f);
      return () => subs.delete(f);
    },
  };
}

export function useStore<T, S>(store: Store<T>, sel: (s: T) => S): S {
  return useSyncExternalStore(store.subscribe, () => sel(store.get()));
}

export interface User { id: string; username: string; display: string; color: string; admin: boolean }

export interface Recs {
  page: Record<string, Page>;
  obj: Record<string, Obj>;
  char: Record<string, Char>;
  handout: Record<string, Handout>;
  macro: Record<string, Macro>;
  table: Record<string, RollTable>;
  track: Record<string, Track>;
  deck: Record<string, Deck & { pile: any }>;
}
const emptyRecs = (): Recs => ({ page: {}, obj: {}, char: {}, handout: {}, macro: {}, table: {}, track: {}, deck: {} });

export interface GameStore {
  user: User | null;
  connected: boolean;
  me: string;
  game: GameInfo | null;
  members: Member[];
  online: Presence[];
  recs: Recs;
  chat: ChatMsg[];
  chatMore: boolean;
  error: string | null;
}

export const G = createStore<GameStore>({
  user: null, connected: false, me: "", game: null, members: [], online: [], recs: emptyRecs(), chat: [], chatMore: true, error: null,
});

export function resetGame() {
  G.set({ connected: false, me: "", game: null, members: [], online: [], recs: emptyRecs(), chat: [], chatMore: true, error: null });
}

export type Tool = "select" | "pan" | "ruler" | "draw" | "text" | "fog" | "wall" | "door";

export interface Win {
  id: string;
  kind: "char" | "handout" | "token" | "page" | "table" | "deck" | "help" | "import";
  ref: string;
  x: number;
  y: number;
  w: number;
  h: number;
  z: number;
}

export interface UIStore {
  tool: Tool;
  drawShape: "free" | "line" | "rect" | "ellipse" | "poly";
  fogShape: "rect" | "poly";
  fogMode: "reveal" | "hide";
  rulerShape: "line" | "circle" | "cone" | "square";
  layer: Layer;
  selected: string[];
  viewPage: string | null;
  stroke: string;
  fill: string;
  strokeWidth: number;
  fontSize: number;
  windows: Win[];
  tab: "chat" | "art" | "journal" | "jukebox" | "collection" | "settings";
  speakAs: string; // "" = me, or char id
  gmDark: number; // GM lighting preview opacity 0..1
  turnOpen: boolean;
  sideOpen: boolean;
  volume: number;
  handOpen: boolean;
}

const saved = (() => {
  try { return JSON.parse(localStorage.getItem("hearth_ui") || "{}"); } catch { return {}; }
})();

export const UI = createStore<UIStore>({
  tool: "select", drawShape: "free", fogShape: "rect", fogMode: "reveal", rulerShape: "line", layer: "objects",
  selected: [], viewPage: null, stroke: "#d02020", fill: "transparent", strokeWidth: 4, fontSize: 28,
  windows: [], tab: "chat", speakAs: "", gmDark: 0, turnOpen: false, sideOpen: true, volume: 60, handOpen: false,
  ...pick(saved, ["stroke", "fill", "strokeWidth", "fontSize", "tab", "volume", "sideOpen"]),
});
UI.subscribe(() => {
  try { localStorage.setItem("hearth_ui", JSON.stringify(pick(UI.get(), ["stroke", "fill", "strokeWidth", "fontSize", "tab", "volume", "sideOpen"]))); } catch {}
});

function pick(o: any, keys: string[]) {
  const r: any = {};
  for (const k of keys) if (o && k in o) r[k] = o[k];
  return r;
}

// ---- derived helpers ----
export const isGM = () => {
  const s = G.get();
  return s.members.find(m => m.uid === s.me)?.role === "gm";
};
export const useIsGM = () => useStore(G, s => s.members.find(m => m.uid === s.me)?.role === "gm");

export function playerPageOf(uid: string): string {
  const g = G.get().game;
  if (!g) return "";
  return g.state.playerPages[uid] || g.state.playerPage;
}
export function currentPageId(): string {
  const s = G.get();
  if (!s.game) return "";
  if (isGM()) {
    const v = UI.get().viewPage;
    if (v && s.recs.page[v]) return v;
    return s.game.state.playerPage;
  }
  return playerPageOf(s.me);
}
export function useCurrentPageId(): string {
  useStore(G, s => s.game?.state);
  useStore(G, s => s.recs.page);
  useStore(UI, s => s.viewPage);
  useStore(G, s => s.members);
  return currentPageId();
}

const inList = (l: string[] | undefined, uid: string) => !!l && (l.includes("all") || l.includes(uid));
export function controlsChar(ch: Char | undefined, uid = G.get().me) {
  return !!ch && (isGM() || inList(ch.controlledBy, uid));
}
export function controlsObj(o: Obj, uid = G.get().me): boolean {
  if (isGM()) return true;
  if (o.owner === uid) return true;
  if (inList(o.controlledBy, uid)) return true;
  if (o.represents) {
    const ch = G.get().recs.char[o.represents];
    return !!ch && inList(ch.controlledBy, uid);
  }
  return false;
}
export function memberName(uid: string) {
  return G.get().members.find(m => m.uid === uid)?.display ?? "?";
}

export function openWindow(kind: Win["kind"], ref: string, size?: { w: number; h: number }) {
  const ws = UI.get().windows;
  const z = Math.max(100, ...ws.map(w => w.z)) + 1;
  const ex = ws.find(w => w.kind === kind && w.ref === ref);
  if (ex) {
    UI.set({ windows: ws.map(w => (w === ex ? { ...w, z } : w)) });
    return;
  }
  const w = size?.w ?? (kind === "char" ? 760 : kind === "token" ? 520 : 480);
  const h = size?.h ?? (kind === "char" ? 720 : 560);
  const n = ws.length;
  const x = Math.max(10, Math.min(window.innerWidth - w - 380, 120 + n * 28));
  const y = Math.max(10, Math.min(window.innerHeight - h - 20, 60 + n * 28));
  UI.set({ windows: [...ws, { id: Math.random().toString(36).slice(2), kind, ref, x, y, w, h, z }] });
}
export function closeWindow(id: string) {
  UI.set({ windows: UI.get().windows.filter(w => w.id !== id) });
}

export type { Col };
